import {
  MemberIndex,
  MemberIndexEntry,
  MemberRow,
  MemberSubsetScope,
  leafKeysUnder,
  parentKeysOf,
  ROW_UID_DELIMITER,
  rowUid
} from 'src/components/star-grid/utils/subset-editor/memberSubset';

/**
 * One entry of the subset, which is an ORDERED LIST — TM1's set, not a tree.
 *
 * `parentUid` names the row this entry was inserted under, and it is a HINT, not a fact: if that row is
 * no longer reachable when the list is walked, this entry detaches to level 0. That single rule is what
 * makes `TM1ROLLUP` work — splicing a member's parents in ahead of it severs its link to the rollup it
 * sat in, and its former siblings detach along with it, with no bespoke hiding or re-levelling.
 *
 * Verified against Arc: `Parent` on `Contribution Profit` in a view of
 * `Operating Margin > Operating Income > (Contribution Profit, Total Fixed Costs)` leaves
 * `Operating Income` drawn closed and drops `Total Fixed Costs` to level 0, purely as a consequence of
 * the insertion.
 */
export interface SubsetEntry {
  /** Unique per row, derived from hierarchy position — never an index, or a splice would rename rows. */
  readonly uid: string;
  /** Position key. Membership and operation replay still address rows by position. */
  readonly key: string;
  readonly member: string;
  readonly label: string;
  readonly isLeaf: boolean;
  readonly path: readonly string[];
  readonly parentUid?: string;
  /** Signed weight of the edge into this position — see `PnlHierarchyMember.weight`. */
  readonly weight?: number | null;
  /** Spliced in by `Parent`, so it is never seeded open and never reports its expansion. */
  readonly added?: boolean;
}

/**
 * Adapt a row builder's output into entries WITHOUT reinterpreting it.
 *
 * `displayPath` is already the chain of row keys the builder chose, so the parent link is simply its own
 * prefix and `entryRows` recomputes each depth as `displayPath.length - 1` — exactly what the builder
 * assigned. That equality is what lets Keep / Remove / All keep their current implementations while the
 * list becomes the source of truth for order.
 */
export const toEntries = (rows: readonly MemberRow[]): SubsetEntry[] =>
  rows.map((row) => ({
    uid: row.uid,
    key: row.key,
    member: row.id,
    label: row.label,
    isLeaf: row.isLeaf,
    path: row.path,
    weight: row.weight,
    parentUid: row.displayPath.length > 1 ? rowUid(row.displayPath.slice(0, -1)) : undefined
  }));

// ─── Keep and All, as list transforms ────────────────────────────────────────

/** An entry detached to level 0. The key is ABSENT, not undefined, so equality checks stay simple. */
const withoutParent = (entry: SubsetEntry): SubsetEntry => {
  if (entry.parentUid === undefined) return entry;
  const { parentUid: _dropped, ...rest } = entry;
  return rest;
};

/**
 * Give every entry its ROLLUP, taken from the hierarchy, collapsed.
 *
 * This is what makes a saved view the same KIND of list browsing produces, and it is the reason every
 * operation behaves identically in both. Browsing hands the grid every position in the hierarchy
 * (`visibleMemberRows(labelled, allPositionKeys(index))`) and lets the grid hide the collapsed ones, so an
 * operation always sees the whole tree. A view used to hand over only its STORED rows and rely on `drillAt`
 * to fetch children when a row was clicked — expansion was lazy there and eager here. Since the operations
 * read the list, they saw a full hierarchy while browsing and a handful of rows in a view: `Keep Leaves` on a
 * view saved with its rollups closed returned NOTHING, because the leaves had not been clicked into
 * existence yet.
 *
 * Nothing new becomes VISIBLE. The added rows are absent from `expanded`, so the grid draws them closed, and
 * membership is the visible rows — so a view opens looking exactly as it was saved.
 *
 * Filled in ONLY under a row whose subtree the view left UNEXPLORED — no stored children, and not stored
 * open. That restriction is what keeps this invisible rather than additive. A row the view stored OPEN already
 * states what is under it, so filling its gaps would resurrect the siblings the author had removed: a view
 * showing `Alpha` open with one of its two children would reopen showing both. An imported view stores no
 * expansion at all, so every row there qualifies.
 *
 * A member already on screen elsewhere is emitted again here rather than suppressed. One position legitimately
 * appears at several places — that is what `Parent` does — and uids derive from the row above, so each
 * occurrence is distinct without a dedupe pass.
 */
export const materialiseRollups = (
  entries: readonly SubsetEntry[],
  index: MemberIndex,
  /** Position keys the view stored as OPEN. Their children are whatever the view stored, never more. */
  storedOpen: ReadonlySet<string>
): SubsetEntry[] => {
  const childrenOf = new Map<string, SubsetEntry[]>();
  const roots: SubsetEntry[] = [];
  for (const entry of entries) {
    if (entry.parentUid === undefined) roots.push(entry);
    else childrenOf.set(entry.parentUid, [...(childrenOf.get(entry.parentUid) ?? []), entry]);
  }
  const out: SubsetEntry[] = [];
  const emit = (entry: SubsetEntry): void => {
    out.push(entry);
    const stored = childrenOf.get(entry.uid) ?? [];
    for (const child of stored) emit(child);
    if (stored.length > 0 || storedOpen.has(entry.key)) return;
    for (const childKey of index.get(entry.key)?.childKeys ?? []) emitFromIndex(index, childKey, entry.uid, out);
  };
  for (const root of roots) emit(root);
  return out;
};

/**
 * One position and its whole subtree, read from the hierarchy and hung under `parentUid`.
 *
 * The FULL subtree, not one level, because browsing carries every position and the point here is to match it.
 *
 * `added` is deliberately NOT set: that flag stops a `Parent` copy from sharing the original's expansion
 * seed, whereas these rows are an ordinary part of the tree and must seed and report expansion normally.
 */
const emitFromIndex = (index: MemberIndex, key: string, parentUid: string, out: SubsetEntry[]): void => {
  const entry = index.get(key);
  if (entry === undefined) return;
  const uid = `${parentUid}${ROW_UID_DELIMITER}${key}`;
  out.push({
    uid,
    key,
    member: entry.id,
    label: entry.label,
    isLeaf: entry.isLeaf,
    path: entry.path,
    weight: entry.weight,
    parentUid
  });
  for (const childKey of entry.childKeys) emitFromIndex(index, childKey, uid, out);
};

/**
 * The Keep family: the rows an operation produces, built FROM THE HIERARCHY.
 *
 * Deliberately does not take the list. That is the whole point — a function that cannot see the current row
 * set cannot behave differently for a saved view than for the browsed hierarchy, so the two modes agree
 * structurally rather than by maintenance. Resolving the target against the list instead is what made `Keep`
 * return an empty pane in a view: the anchor of a row `Parent` had spliced in was nowhere in the view's own
 * entries, and clearing the op log took the row away with it.
 *
 * Verified against Arc on `das_mdm/Account`, three observations:
 *   * `Keep + Children` then `Keep Leaves` yields the anchor's LEAVES — rows that were not in the pane, so an
 *     operation re-derives from the dimension rather than filtering what is on screen;
 *   * `Keep + Leaves` then `Keep + Children` brings the intermediate consolidations BACK, so an operation does
 *     not preserve the arrangement the previous one produced;
 *   * `Keep` on a member reached only by expanding a spliced `Parent` retains it with its subtree.
 * The MDX agrees: every operation names its target as a dimension member reference
 * (`[FPA Deck Product].[FPA Deck Product].[Other & BEA^Product Summary - Program Investments^3016]`), never as
 * a position in the current set, while the SET composes through nested `DRILLDOWNMEMBER`/`EXCEPT` — which is
 * why `applyOps` and `excludeEntries` still transform the list and only this does not.
 *
 * Anchors are POSITION keys, so `Keep` acts where you clicked: a multi-parent member has a different key under
 * each parent, and keeping the leaf under one rollup leaves its copy under the other behind.
 *
 * The three branches are the operations themselves:
 *   * `leavesOnly` flattens to the leaves, the anchor deliberately absent;
 *   * `keepLeaves` hangs every leaf DIRECTLY off the anchor, collapsing the intermediate consolidations away
 *     however deep they really sit;
 *   * `keep` / `keepChildren` / `keepDescendants` all emit the real subtree and differ only in how much of it
 *     `seedExpansion` opens, so one walk serves all three.
 *
 * The whole subtree is emitted whatever the expansion, because the grid owns expand/collapse and needs the
 * children of a closed rollup present to draw its expander.
 */
export const scopeEntries = (index: MemberIndex, scope: MemberSubsetScope): SubsetEntry[] => {
  const out: SubsetEntry[] = [];
  const seen = new Set<string>();
  const push = (key: string, isLeaf: boolean, parentKeys: readonly string[]): void => {
    const entry = index.get(key);
    if (entry === undefined || seen.has(key)) return;
    seen.add(key);
    out.push({
      uid: rowUid([...parentKeys, key]),
      key,
      member: entry.id,
      label: entry.label,
      isLeaf,
      path: entry.path,
      weight: entry.weight,
      ...(parentKeys.length > 0 ? { parentUid: rowUid(parentKeys) } : {})
    });
  };

  for (const anchor of scope.anchors) {
    const anchorEntry = index.get(anchor);
    if (anchorEntry === undefined) continue;

    if (scope.op === 'leavesOnly') {
      // A flat list of leaves; the anchor is deliberately absent, so each leaf is its own top.
      for (const leafKey of leafKeysUnder(index, anchor)) push(leafKey, true, []);
      continue;
    }

    if (scope.op === 'keepLeaves') {
      const leaves = leafKeysUnder(index, anchor);
      // Drawn as a leaf when it has none, so the grid offers no expander onto nothing.
      push(anchor, anchorEntry.isLeaf || leaves.length === 0, []);
      for (const leafKey of leaves) if (leafKey !== anchor) push(leafKey, true, [anchor]);
      continue;
    }

    const walk = (key: string, parentKeys: readonly string[]): void => {
      const entry = index.get(key);
      if (entry === undefined) return;
      push(key, entry.isLeaf, parentKeys);
      for (const child of entry.childKeys) walk(child, [...parentKeys, key]);
    };
    walk(anchor, []);
  }
  return out;
};

/**
 * `All` as a list transform: every DISTINCT member once, at level 0 — Prophecy's `TM1SUBSETALL`.
 *
 * Deduped by member, so a member holding several positions collapses to ONE row keyed by the first: the
 * flattening removes the parent, which is the only thing that told two copies apart.
 *
 * Ordered by label, matching `flatMemberRows`. TM1 returns insertion order, which the closure does not
 * carry, so alphabetical is the best available and at least makes a member findable.
 *
 * Emits no children, unlike `flatMemberRows` — `annotateDrillable` supplies the expander from the
 * position's children, and expanding goes through `drillAt`. See the `All` note in
 * star-grid-subset-views.md for why the two implementations still differ.
 */
export const flattenEntries = (entries: readonly SubsetEntry[]): SubsetEntry[] => {
  const byMember = new Map<string, SubsetEntry>();
  for (const entry of entries) if (!byMember.has(entry.member)) byMember.set(entry.member, entry);
  return [...byMember.values()]
    .sort((left, right) => left.label.toLowerCase().localeCompare(right.label.toLowerCase()) || left.member.localeCompare(right.member))
    .map(withoutParent);
};

/**
 * One recorded edit to the list.
 *
 * A single ORDERED log rather than one list per kind: drills and rollups interleave, and order decides
 * what a later op finds — a drill can introduce an earlier entry carrying the same position key that a
 * later rollup would then target.
 */
export type SubsetOp = { readonly kind: 'rollup'; readonly key: string } | { readonly kind: 'drill'; readonly uid: string };

/**
 * Drop removed ROWS from the list, after the ops have been replayed.
 *
 * Keyed on `uid`, not on the position key, and that is the point. `Parent` and drill both put the SAME
 * position on screen twice, so a position key names two rows and cannot express "remove this one" — removing
 * `Advertising` under one parent took it from the other placement instead.
 *
 * Applied AFTER `applyOps` for the same reason: excluding first let the rollup splice re-introduce a row
 * carrying the removed key, so the removal silently undid itself.
 *
 * Survivors are RE-PARENTED onto the nearest ancestor that is still there, which is what makes removing an
 * expanded member lift its children exactly one level instead of dropping them to level 0. Letting
 * `entryRows` detach them would be the other documented rule — the one `Parent` relies on — and it is wrong
 * here: you can see those children, so removing them was not what was asked.
 *
 * The walk climbs past a CHAIN of removed ancestors, so removing a parent and a grandparent together lifts
 * the survivors once, not twice. Reaching the top with nothing left leaves `parentUid` undefined, i.e. level
 * 0, which is the correct answer when every ancestor has gone.
 */
export const excludeEntries = (entries: readonly SubsetEntry[], excluded: ReadonlySet<string>): SubsetEntry[] => {
  if (excluded.size === 0) return [...entries];
  const parentOf = new Map(entries.map((entry) => [entry.uid, entry.parentUid]));
  const survivingParent = (uid: string | undefined): string | undefined => {
    let current = uid;
    while (current !== undefined && excluded.has(current)) current = parentOf.get(current);
    return current;
  };
  return entries
    .filter((entry) => !excluded.has(entry.uid))
    .map((entry) => {
      const parentUid = survivingParent(entry.parentUid);
      if (parentUid === entry.parentUid) return entry;
      const { parentUid: _dropped, ...rest } = entry;
      return parentUid === undefined ? rest : { ...rest, parentUid };
    });
};

/**
 * The rows BENEATH the given ones, read from each row's display chain.
 *
 * Read from `displayPath`, NOT by matching uid strings. A uid usually is its display chain joined, which made a
 * `startsWith` prefix test look equivalent — but it is not, and the difference is a real bug both ways:
 *
 *   * `drillAt` prefixes a drilled child with `drill:`, so its uid is `drill:<parent>><key>` and does not start
 *     with `<parent>>`. Remove + Descendants therefore left every drilled child behind — visible whenever
 *     `Parent` had severed a row's run and the user reopened it.
 *   * worse, the prefix test MATCHED the occurrence still sitting further down the list, whose uid genuinely
 *     does start with that string. So it removed a row nobody pointed at while sparing the ones it was aimed at.
 *
 * `displayPath` is built by `entryRows` from the parent links, so it states ancestry directly however a uid was
 * spelled.
 */
export const descendantUids = (uids: readonly string[], rows: readonly MemberRow[]): string[] => {
  const targets = new Set(uids);
  return rows.filter((row) => !targets.has(row.uid) && row.displayPath.slice(0, -1).some((segment) => targets.has(segment))).map((row) => row.uid);
};

/**
 * Replay the op log over the built list.
 *
 * `rollup` is `Parent` (TM1ROLLUP): splice the member's parents in AT the clicked entry, at level 0. The
 * clicked entry is neither moved nor removed — `TM1ROLLUP`'s first argument is the set UNCHANGED, and
 * the MDX Arc emits shows the clicked member still at its nested path. It detaches to level 0 on its
 * own, in the walk, because the rows just inserted put its parent out of reach.
 *
 * `drill` is expand (DRILLDOWNMEMBER). In order, because each op has to find its row in the list the
 * previous ones left behind.
 *
 * Every op targets a row the BUILDERS produced, never one an earlier op inserted. `emitSubtree` carries
 * the clicked member inside its parent's subtree, so a plain search by key could match a copy an earlier
 * op created — splicing into this function's own output, at the wrong depth, and (for two ops on the same
 * member) emitting uids that collide.
 */
export const applyOps = (entries: readonly SubsetEntry[], index: MemberIndex, ops: readonly SubsetOp[]): SubsetEntry[] => {
  let next = [...entries];
  const insertedUids = new Set<string>();
  ops.forEach((op, opIndex) => {
    if (op.kind === 'drill') {
      next = drillAt(next, index, op.uid, insertedUids);
      return;
    }
    const at = next.findIndex((entry) => entry.key === op.key && !insertedUids.has(entry.uid));
    if (at === -1) return;
    const inserted: SubsetEntry[] = [];
    /*
     * Each parent brings its subtree, collapsed. A grid draws no expander for a row it can see no
     * children for, and Arc shows every inserted parent as expandable — the grid hides a collapsed
     * parent's children itself, so the extra rows cost nothing to render.
     *
     * The prefix carries the op's POSITION IN THE LOG, which is what keeps two rollups on the same
     * member distinguishable rather than emitting identical uids for AG Grid to silently drop. The log is
     * append-only and cleared wholesale, so these indices are stable across renders and row identity
     * holds.
     */
    for (const parentKey of parentKeysOf(index, op.key)) emitSubtree(index, parentKey, `rollup:${opIndex}:${op.key}`, undefined, inserted);
    if (inserted.length === 0) return;
    for (const entry of inserted) insertedUids.add(entry.uid);
    next = [...next.slice(0, at), ...inserted, ...next.slice(at)];
  });
  return next;
};

/**
 * Expand (DRILLDOWNMEMBER): splice the member's children in directly AFTER the row, attached to it.
 *
 * This is how a row whose children were severed by a splice gets back into its rollup. It also means
 * expansion INSERTS children rather than restoring whatever used to be beneath the row — which is why
 * re-expanding after `Keep + Leaves` yields the rollups instead of the leaves, as Arc does.
 */
const drillAt = (entries: readonly SubsetEntry[], index: MemberIndex, uid: string, insertedUids: Set<string>): SubsetEntry[] => {
  // By uid, so this already addresses one exact row — but its own insertions are recorded all the same,
  // so a later rollup cannot target them.
  const at = entries.findIndex((entry) => entry.uid === uid);
  if (at === -1) return [...entries];
  const parent = index.get(entries[at].key);
  if (!parent) return [...entries];
  const inserted: SubsetEntry[] = [];
  for (const childKey of parent.childKeys) {
    const child = index.get(childKey);
    if (!child) continue;
    inserted.push({
      /*
       * `drill:` prefixed, not just parented by the drilled row's uid.
       *
       * The builder's natural uid for a child is its display-path join — which for a child of THIS row
       * at THIS position is the same string, so the unprefixed form collided with the occurrence still
       * sitting further down the list, and AG Grid silently drops rows whose ids collide.
       */
      uid: `drill:${uid}${ROW_UID_DELIMITER}${childKey}`,
      key: childKey,
      member: child.id,
      label: child.label,
      isLeaf: child.isLeaf,
      path: child.path,
      weight: child.weight,
      parentUid: uid
    });
  }
  for (const entry of inserted) insertedUids.add(entry.uid);
  return [...entries.slice(0, at + 1), ...inserted, ...entries.slice(at + 1)];
};

/**
 * Mark rows the grid would draw as leaves but whose member has a rollup.
 *
 * Run LAST, on the rows the grid actually receives: a search can filter a row's children away after the
 * walk has placed them. "Has children" is read off the surviving display paths rather than by looking at
 * the next row, so it holds however the rows were filtered or reordered.
 */
export const annotateDrillable = (rows: readonly MemberRow[], index: MemberIndex): MemberRow[] => {
  const withChildren = new Set<string>();
  for (const row of rows) for (const segment of row.displayPath.slice(0, -1)) withChildren.add(segment);
  return rows.map((row) => (!withChildren.has(row.uid) && (index.get(row.key)?.childKeys.length ?? 0) > 0 ? { ...row, canDrill: true } : row));
};

/**
 * Emit one member and its whole subtree as entries, under a uid `prefix`.
 *
 * The prefix keeps these uids distinct from the same members' rows elsewhere in the list: a spliced
 * parent is a different ROW from the occurrence it was reached through, even though both address the
 * same position.
 */
const emitSubtree = (index: MemberIndex, key: string, prefix: string, parentUid: string | undefined, out: SubsetEntry[]): void => {
  const entry: MemberIndexEntry | undefined = index.get(key);
  if (!entry) return;
  const uid = `${prefix}${ROW_UID_DELIMITER}${key}`;
  out.push({
    uid,
    key,
    member: entry.id,
    label: entry.label,
    isLeaf: entry.isLeaf,
    path: entry.path,
    weight: entry.weight,
    parentUid,
    added: true
  });
  for (const childKey of entry.childKeys) emitSubtree(index, childKey, prefix, uid, out);
};

/**
 * Derive depth and the grid's tree path from ORDER. The whole display model is this loop.
 *
 * Depth is not stored anywhere: it is a function of where a row sits relative to the row it claims as
 * its parent. That is why an insertion re-levels everything downstream of it for free, and why no
 * operation has to reason about levels.
 */
export const entryRows = (entries: readonly SubsetEntry[]): MemberRow[] => {
  const rows: MemberRow[] = [];
  const stack: { uid: string; depth: number; displayPath: readonly string[] }[] = [];
  for (const entry of entries) {
    // Unwind until the row above is the one this entry claims as its parent. Anything spliced between
    // the two ends the run, which is what drops a severed entry — and its siblings — to level 0.
    while (stack.length > 0 && stack[stack.length - 1].uid !== entry.parentUid) stack.pop();
    const parent = stack[stack.length - 1];
    const depth = parent === undefined ? 0 : parent.depth + 1;
    const displayPath = parent === undefined ? [entry.uid] : [...parent.displayPath, entry.uid];
    rows.push({
      uid: entry.uid,
      key: entry.key,
      id: entry.member,
      label: entry.label,
      isLeaf: entry.isLeaf,
      depth,
      path: entry.path,
      displayPath,
      weight: entry.weight,
      ...(entry.added === true ? { added: true } : {})
    });
    stack.push({ uid: entry.uid, depth, displayPath });
  }
  return rows;
};
